/**
 * VAST Discount Module
 * --------------------
 * Handles discount code application in the cart drawer using Shopify's
 * native Cart API. Hooks into Dawn's cart.js section rendering so that
 * line totals, line-level discount badges, and subtotals all update
 * after applying or removing a code.
 *
 * CHANGELOG (Okt 2026, Test):
 * - VORABRUF-SKIP: apply() holt /cart.js nur noch, wenn die Pills
 *   aktive manuelle Codes zeigen (getActivePillCodes). Ohne Pills geht
 *   es direkt zu "allein anwenden". Tracking-Wert faellt dann auf
 *   cart.total_discount zurueck. Entfernen/Blocklist unveraendert.
 *
 * CHANGELOG (Sep 2026, Free Shipping + Performance):
 * - PILL-SYNC NACH DAWN-RE-RENDERS: Test 19.09. — die Free-Shipping-Pill
 *   verschwand nach einer Mengenänderung, weil dort Dawns cart.js den
 *   Drawer neu rendert und Liquid den Code nicht kennt (keine Shipping
 *   Line im Cart → keine Allokation). ensurePills läuft deshalb jetzt
 *   auch (a) auf PUB_SUB_EVENTS.cartUpdate — mit der change.js-Antwort
 *   als cartData ohne Extra-Request, bei add.js (kein discount_codes im
 *   Payload) mit einem /cart.js-Fetch — und (b) einmal pro Seitenaufruf
 *   beim ersten Öffnen des Drawers (Initialzustand nach Reload).
 * - CHANGE-STOP AM INPUT: das native change-Event des Discount-Inputs
 *   wird gestoppt. Nötig für das TOP-2-Layout, in dem das Feld innerhalb
 *   von <cart-drawer-items> liegt — Dawns CartItems-Listener würde es
 *   sonst als Mengenänderung werten (Step-Fehler-Tooltip). Im Footer-
 *   Layout wirkungslos, daher in beiden Varianten einsetzbar.
 * - PILL-FALLBACK: nach jedem Section-Re-Render werden die Pills gegen
 *   cart.discount_codes (Ajax API, ist in der update.js-Antwort schon
 *   enthalten — kein Extra-Request) abgeglichen. Applicable Codes, die
 *   das Liquid-Snippet nicht gerendert hat (z.B. Free-Shipping-Codes
 *   ohne Line-/Cart-Allokation), werden clientseitig als Manual-Pill
 *   nachgetragen. Primär rendert das Snippet sie jetzt selbst über
 *   cart.discount_applications (target_type 'shipping_line').
 * - OBSERVER-SCOPE: der Re-Init-MutationObserver hängt nicht mehr an
 *   document.body, sondern am <cart-drawer> (Fallback body, falls kein
 *   Drawer existiert). Die Abfrage läuft nur im beobachteten Knoten.
 *
 * CHANGELOG (Sep 2026):
 * - ZOOM LOCK AM DRAWER: maximum-scale=1 wird gesetzt, sobald der Cart
 *   Drawer öffnet (Klasse "active"), und entfernt, wenn er schließt —
 *   ein einziger Observer für beides. Ersetzt den bisherigen Lock auf
 *   touchstart des Inputs (Race zum Fokus bei schnellem Tap) und den
 *   Unlock in setLoading(false) (entsperrte im Fehlerpfad bei noch
 *   fokussiertem Feld → iOS zoomte beim Zurückschreiben des Viewports).
 *   Beide Viewport-Writes passieren jetzt an Class-Mutationen, also
 *   nie mitten in einem Tap. Pinch-Zoom ist damit bei offenem Drawer
 *   deaktiviert (Dcart-Verhalten "disable pinch zoom"), sonst unberührt.
 *   Hinweis: der Zoom-Schutz war nie statisch in theme.liquid (alter
 *   Kommentar unten ist überholt).
 *
 * CHANGELOG (Jul 2026):
 * - AFFILIATE GUARD: manually removed or replaced codes are added to a
 *   session blocklist. Affiliate apps with auto-apply (UpPromote /
 *   Secomapp) re-inject their code on every page load and OVERWRITE
 *   manually applied codes. On each page load we strip blocked codes
 *   from the cart, and on remove/replace we also clear the app's
 *   client-side traces (localStorage/sessionStorage/cookies). User
 *   intent always wins.
 * - ZOOM: (überholt, siehe Sep 2026) — Kernregel bleibt: keine
 *   Viewport-Writes auf blur oder mitten in einem Tap (they swallow the
 *   tap on the apply button when blur fires mid-touch).
 * - COOKIE SYNC: /discount/{code} after manual apply so the checkout
 *   uses the manually entered code instead of a stale affiliate cookie.
 * - REPLACE INSTEAD OF ERROR: a code that isn't combinable with the
 *   active one replaces it (combine first, then apply alone).
 * - UNIFIED ERROR: the Storefront Cart API cannot distinguish a
 *   nonexistent code from an existing-but-not-qualifying one (Shopify
 *   persists ANY string with applicable:false; apps resolve this via
 *   the Admin API). One unified message covers both cases.
 * - Section re-render uses Dawn's own pattern so the drawer structure
 *   survives; error paths never re-render before showing the message.
 */

(function () {
  'use strict';

  const SELECTORS = {
    root: '[data-vast-discount]',
    input: '[data-vast-discount-input]',
    submit: '[data-vast-discount-submit]',
    submitLabel: '[data-vast-discount-submit-label]',
    spinner: '[data-vast-discount-spinner]',
    message: '[data-vast-discount-message]',
    tags: '[data-vast-discount-tags]',
    tag: '[data-vast-discount-tag]',
    remove: '[data-vast-discount-remove]',
  };

  /**
   * Localized error messages. Language is read from <html lang="…">,
   * matching the shop's Shopify locale. Fallback: de.
   */
  const TRANSLATIONS = {
    de: {
      invalid:        'Der eingegebene Code ist nicht auf die Artikel in deinem Warenkorb anwendbar.',
      expired:        'Dieser Rabattcode ist abgelaufen oder nicht mehr verfügbar.',
      notCombinable:  'Dieser Code ist nicht mit deinem aktiven Rabatt kombinierbar.',
      minimumNotMet:  'Mindestbestellwert nicht erreicht.',
      customerOnly:   'Dieser Code ist nur für eingeloggte Kunden verfügbar.',
      productMismatch:'Der eingegebene Code ist nicht auf die Artikel in deinem Warenkorb anwendbar.',
      usageLimit:     'Dieser Code hat sein Nutzungslimit erreicht.',
      networkError:   'Verbindungsfehler. Bitte versuche es erneut.',
      alreadyApplied: 'Dieser Code ist bereits aktiv.',
      empty:          'Bitte gib einen Code ein.',
      removeCode:     'Code entfernen',
    },
    en: {
      invalid:        'The code entered cannot be applied to the items in your cart.',
      expired:        'This discount code has expired or is no longer available.',
      notCombinable:  'This code cannot be combined with your active discount.',
      minimumNotMet:  'Minimum order value not reached.',
      customerOnly:   'This code is only available for logged-in customers.',
      productMismatch:'The code entered cannot be applied to the items in your cart.',
      usageLimit:     'This code has reached its usage limit.',
      networkError:   'Connection error. Please try again.',
      alreadyApplied: 'This code is already active.',
      empty:          'Please enter a code.',
      removeCode:     'Remove code',
    },
    fr: {
      invalid:        'Le code saisi n\'est pas applicable aux articles de votre panier.',
      expired:        'Ce code de réduction a expiré ou n\'est plus disponible.',
      notCombinable:  'Ce code ne peut pas être combiné avec votre réduction active.',
      minimumNotMet:  'Montant minimum de commande non atteint.',
      customerOnly:   'Ce code est réservé aux clients connectés.',
      productMismatch:'Le code saisi n\'est pas applicable aux articles de votre panier.',
      usageLimit:     'Ce code a atteint sa limite d\'utilisation.',
      networkError:   'Erreur de connexion. Veuillez réessayer.',
      alreadyApplied: 'Ce code est déjà actif.',
      empty:          'Veuillez saisir un code.',
      removeCode:     'Supprimer le code',
    },
    it: {
      invalid:        'Il codice inserito non è applicabile agli articoli nel carrello.',
      expired:        'Questo codice sconto è scaduto o non è più disponibile.',
      notCombinable:  'Questo codice non è combinabile con lo sconto attivo.',
      minimumNotMet:  'Valore minimo dell\'ordine non raggiunto.',
      customerOnly:   'Questo codice è disponibile solo per i clienti registrati.',
      productMismatch:'Il codice inserito non è applicabile agli articoli nel carrello.',
      usageLimit:     'Questo codice ha raggiunto il limite di utilizzo.',
      networkError:   'Errore di connessione. Riprova.',
      alreadyApplied: 'Questo codice è già attivo.',
      empty:          'Inserisci un codice.',
      removeCode:     'Rimuovi codice',
    },
    es: {
      invalid:        'El código introducido no es aplicable a los artículos de tu carrito.',
      expired:        'Este código de descuento ha caducado o ya no está disponible.',
      notCombinable:  'Este código no se puede combinar con tu descuento activo.',
      minimumNotMet:  'No se ha alcanzado el pedido mínimo.',
      customerOnly:   'Este código solo está disponible para clientes registrados.',
      productMismatch:'El código introducido no es aplicable a los artículos de tu carrito.',
      usageLimit:     'Este código ha alcanzado su límite de uso.',
      networkError:   'Error de conexión. Inténtalo de nuevo.',
      alreadyApplied: 'Este código ya está activo.',
      empty:          'Introduce un código.',
      removeCode:     'Eliminar código',
    },
    nl: {
      invalid:        'De ingevoerde code is niet van toepassing op de artikelen in je winkelwagen.',
      expired:        'Deze kortingscode is verlopen of niet meer beschikbaar.',
      notCombinable:  'Deze code kan niet worden gecombineerd met je actieve korting.',
      minimumNotMet:  'Minimale bestelwaarde niet bereikt.',
      customerOnly:   'Deze code is alleen beschikbaar voor ingelogde klanten.',
      productMismatch:'De ingevoerde code is niet van toepassing op de artikelen in je winkelwagen.',
      usageLimit:     'Deze code heeft de gebruikslimiet bereikt.',
      networkError:   'Verbindingsfout. Probeer het opnieuw.',
      alreadyApplied: 'Deze code is al actief.',
      empty:          'Voer een code in.',
      removeCode:     'Code verwijderen',
    },
  };

  const PAGE_LANG = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
  const STRINGS = TRANSLATIONS[PAGE_LANG] || TRANSLATIONS.de;

  /* ---------- Cart API helpers ---------- */

  async function fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!res.ok) throw new Error('cart_fetch_failed');
    return res.json();
  }

  /**
   * Updates discounts via /cart/update.js. Pass an array of codes
   * (joined with comma — Shopify supports multiple if combinable).
   * Empty array clears all manual codes.
   *
   * Throws:
   *   err.kind = 'http'    → Shopify rejected the update (e.g. 422)
   *   err.kind = 'network' → fetch itself failed (offline, CORS, …)
   */
  async function updateDiscounts(codes, sectionsToRender) {
    const body = { discount: codes.join(',') };
    if (sectionsToRender && sectionsToRender.length) {
      body.sections = sectionsToRender.join(',');
      body.sections_url = window.location.pathname;
    }

    let res;
    try {
      res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
    } catch (fetchErr) {
      const err = new Error('cart_update_network_failed');
      err.kind = 'network';
      throw err;
    }

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      const err = new Error(errorData.message || 'cart_update_failed');
      err.kind = 'http';
      err.status = res.status;
      err.data = errorData;
      throw err;
    }
    return res.json();
  }

  /**
   * Returns the section IDs the cart-drawer custom element wants
   * re-rendered. Falls back to the standard Dawn names.
   */
  function getCartSections() {
    const cartItems = document.querySelector('cart-drawer-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      try {
        return cartItems.getSectionsToRender().map(s => s.section);
      } catch (_) {}
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function codeIsApplicable(cart, code) {
    return (cart.discount_codes || []).some(
      d => d.applicable && d.code.toUpperCase() === code.toUpperCase()
    );
  }

  /* ---------- Pill fallback (codes without allocation, e.g. free shipping) ---------- */

  const TAG_ICON_SVG =
    '<svg class="vast-discount__tag-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
      '<path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.83 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/>' +
      '<circle cx="7.5" cy="7.5" r="1.3" fill="currentColor"/>' +
    '</svg>';

  const REMOVE_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
      '<path d="M1.5 1.5l10.05 10.05M11.5 1.5L1.45 11.55"/>' +
    '</svg>';

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  /**
   * Ensures every applicable code from cart.discount_codes (Ajax API) has
   * a pill in the DOM. The Liquid snippet can only see discount
   * applications; free-shipping codes allocate to neither lines nor
   * cart level and — depending on Shopify's rendering — may be missing
   * server-side. Runs after each section re-render with the cart JSON
   * that is already part of the update.js response (no extra request).
   *
   * Only ADDS missing manual pills. Never removes: removal is handled by
   * the server-side re-render (a code that is gone won't be rendered).
   */
  function ensurePills(cart) {
    const codes = (cart && cart.discount_codes) || [];
    if (!codes.length) return;

    const tagsEl = document.querySelector(SELECTORS.root + ' ' + SELECTORS.tags);
    if (!tagsEl) return;

    const present = new Set(
      Array.from(tagsEl.querySelectorAll(SELECTORS.tag))
        .map(el => (el.dataset.code || '').toUpperCase())
    );
    // Manual pills sit before automatic ones — keep that order.
    const firstAuto = tagsEl.querySelector('[data-type="automatic"]');

    codes.forEach((d) => {
      if (!d.applicable || !d.code) return;
      const upper = d.code.toUpperCase();
      if (present.has(upper)) return;
      present.add(upper);

      const safe = escapeHtml(d.code);
      const li = document.createElement('li');
      li.className = 'vast-discount__tag';
      li.setAttribute('data-vast-discount-tag', '');
      li.dataset.code = d.code;
      li.dataset.type = 'manual';
      li.innerHTML =
        TAG_ICON_SVG +
        '<span class="vast-discount__tag-label">' + safe + '</span>' +
        '<button type="button" class="vast-discount__tag-remove"' +
          ' aria-label="' + escapeHtml(STRINGS.removeCode) + ': ' + safe + '"' +
          ' data-vast-discount-remove="' + safe + '">' +
          REMOVE_ICON_SVG +
        '</button>';
      tagsEl.insertBefore(li, firstAuto);
    });
  }

  /**
   * Apply Dawn's section-rendering response to the DOM.
   *
   * Uses Dawn's own replacement pattern:
   *   target = getElementById(section.id).querySelector(section.selector)
   *            || getElementById(section.id)
   *   source = parsedHtml.querySelector(section.selector || '#' + section.id)
   *
   * For the cart drawer the selector is '.drawer__inner', so only the
   * inner contents are swapped — overlay, wrapper and open/active
   * classes survive.
   */
  function renderSections(cart) {
    if (!cart || !cart.sections) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
      return;
    }

    const cartItemsEl = document.querySelector('cart-drawer-items');

    if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
      try {
        cartItemsEl.getSectionsToRender().forEach((section) => {
          const sectionHtml = cart.sections[section.section];
          if (!sectionHtml) return;

          const container = document.getElementById(section.id);
          if (!container) return;

          const selector = section.selector || '#' + section.id;
          const targetEl = (section.selector && container.querySelector(section.selector)) || container;

          const newContent = new DOMParser()
            .parseFromString(sectionHtml, 'text/html')
            .querySelector(selector);
          if (newContent) {
            targetEl.innerHTML = newContent.innerHTML;
          }
        });
      } catch (e) {
        // Don't leave the drawer half-broken — Dawn re-renders on the
        // next cart mutation anyway.
      }
    }

    // Pills the Liquid snippet couldn't render (free shipping) —
    // uses the cart JSON already in hand, no extra request.
    ensurePills(cart);

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
  }

  /* ---------- Affiliate guard (blocklist + trace cleanup) ---------- */

  const BLOCK_KEY = 'vast:blocked-codes';

  function getBlockedCodes() {
    try {
      const raw = sessionStorage.getItem(BLOCK_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (_) {
      return [];
    }
  }

  function setBlockedCodes(codes) {
    try {
      sessionStorage.setItem(BLOCK_KEY, JSON.stringify(codes));
    } catch (_) {}
  }

  function blockCode(code) {
    const upper = code.toUpperCase();
    const blocked = getBlockedCodes();
    if (!blocked.includes(upper)) {
      blocked.push(upper);
      setBlockedCodes(blocked);
    }
  }

  function unblockCode(code) {
    const upper = code.toUpperCase();
    setBlockedCodes(getBlockedCodes().filter(c => c !== upper));
  }

  /**
   * Clears client-side traces of affiliate auto-apply systems
   * (UpPromote is built by Secomapp → 'sca' keys; Dcart used
   * 'sc:discount…'). Called when the user explicitly removes or
   * replaces a code, so the app can't re-apply it from its storage.
   *
   * NOTE: this also weakens client-side ambassador ATTRIBUTION for the
   * session — intentional: the user explicitly opted out of the code.
   */
  function clearAffiliateTraces() {
    const matches = (name) => {
      const s = String(name).toLowerCase();
      return (
        s.includes('uppromote') ||
        s.includes('affiliate') ||
        s.includes('dcart') ||
        s.startsWith('sca') ||
        s.includes('sc:discount') ||
        s === 'discount_code'
      );
    };

    [window.localStorage, window.sessionStorage].forEach((store) => {
      try {
        Object.keys(store)
          .filter(matches)
          .forEach(k => store.removeItem(k));
      } catch (_) {}
    });

    try {
      const host = window.location.hostname;
      const bareHost = host.replace(/^www\./, '');
      (document.cookie || '').split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (!name || !matches(name)) return;
        const expire = name + '=; Max-Age=0; path=/';
        document.cookie = expire;
        document.cookie = expire + '; domain=' + host;
        document.cookie = expire + '; domain=.' + bareHost;
      });
    } catch (_) {}
  }

  /**
   * Watchdog: strips blocked codes that an auto-apply script has
   * re-injected into the cart. Runs once per page load. This makes
   * user intent win even when the affiliate app re-applies its code
   * on every navigation (e.g. returning from checkout).
   */
  async function enforceBlockedCodes() {
    const blocked = getBlockedCodes();
    if (!blocked.length) return;

    try {
      const cart = await fetchCart();
      const active = (cart.discount_codes || [])
        .filter(d => d.applicable)
        .map(d => d.code);
      const keep = active.filter(c => !blocked.includes(c.toUpperCase()));
      if (keep.length === active.length) return;

      clearAffiliateTraces();
      const updated = await updateDiscounts(keep, getCartSections());
      renderSections(updated);
    } catch (_) {}
  }

  /* ---------- Zoom lock (Dcart parity, safe unlock points) ---------- */

  /**
   * Suppresses the iOS auto-zoom on the 1.4rem input by adding
   * maximum-scale=1 to the viewport meta WHILE THE DRAWER IS OPEN, and
   * restores the original viewport when it closes.
   *
   * THE ONE HARD RULE: never write the viewport on blur/focusout or
   * mid-tap. blur fires the exact moment the user taps the apply
   * button; a viewport write in that instant makes iOS cancel the tap
   * (broke the button twice). Lock and unlock therefore happen ONLY on
   * the drawer's class mutation ("active" added/removed) — that fires
   * after the open/close tap has been fully processed and long before
   * any tap into the input. No touchstart/focus race, no unlock while
   * the field still has focus.
   */
  const ZoomLock = {
    locked: false,
    original: null,
    meta() {
      return document.querySelector('meta[name="viewport"]');
    },
    lock() {
      if (this.locked) return;
      const meta = this.meta();
      if (!meta) return;
      this.original = meta.getAttribute('content') ||
        'width=device-width, initial-scale=1';
      if (!/maximum-scale/i.test(this.original)) {
        meta.setAttribute('data-original-content', this.original);
        meta.setAttribute('content', this.original + ', maximum-scale=1');
      }
      this.locked = true;
    },
    unlock() {
      if (!this.locked) return;
      const meta = this.meta();
      if (meta && this.original) {
        meta.setAttribute('content', this.original);
        meta.removeAttribute('data-original-content');
      }
      this.locked = false;
    },
  };

  /**
   * Locks the zoom when the drawer opens and unlocks it when it closes.
   * Both are class mutations on <cart-drawer> ("active"), i.e. safe
   * moments: no tap can be in flight. lock()/unlock() are idempotent,
   * so other class toggles (animate, is-empty) are harmless.
   */
  function setupDrawerZoomLock(drawer) {
    if (!drawer) return;
    const sync = () => {
      if (drawer.classList.contains('active')) {
        ZoomLock.lock();
      } else {
        ZoomLock.unlock();
      }
    };
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    sync(); // Drawer könnte beim Boot bereits offen sein
  }

  /* ---------- Pill sync after Dawn re-renders + first open ---------- */

  /**
   * Liquid kann Free-Shipping-Codes nicht rendern (keine Shipping Line im
   * Cart → keine Allokation), deshalb muss ensurePills auch nach Dawns
   * eigenen Re-Renders laufen, nicht nur nach unseren:
   *
   * (a) PUB_SUB_EVENTS.cartUpdate — cart.js publisht nach jedem Qty-Update
   *     die /cart/change.js-Antwort als cartData (enthält discount_codes):
   *     kein Extra-Request. product-form publisht dasselbe Event mit der
   *     /cart/add.js-Antwort (Line Item, KEIN discount_codes) — dann
   *     einmal /cart.js holen. Der Publish erfolgt bei Dawn NACH dem
   *     DOM-Swap, die neuen Pills landen also im frischen Markup.
   *
   * (b) Erstes Öffnen des Drawers pro Seitenaufruf — deckt den
   *     Initialzustand nach einem Reload ab. Danach reicht (a).
   *
   * Nur Lesezugriffe, keine Viewport-/Focus-/Scroll-Writes.
   */
  function setupPillSync(drawer) {
    const syncFromServer = () => fetchCart().then(ensurePills).catch(() => {});

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const data = event && event.cartData;
        if (data && Array.isArray(data.discount_codes)) {
          ensurePills(data);
        } else {
          syncFromServer();
        }
      });
    }

    if (!drawer) return;
    let syncedOnce = false;
    const onOpen = () => {
      if (syncedOnce || !drawer.classList.contains('active')) return;
      syncedOnce = true;
      syncFromServer();
    };
    new MutationObserver(onOpen).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    onOpen();
  }

  /* ---------- Discount form controller ---------- */

  class DiscountForm {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector(SELECTORS.input);
      this.submit = root.querySelector(SELECTORS.submit);
      this.submitLabel = root.querySelector(SELECTORS.submitLabel);
      this.spinner = root.querySelector(SELECTORS.spinner);
      this.messageEl = root.querySelector(SELECTORS.message);
      this.tagsEl = root.querySelector(SELECTORS.tags);
      this.isLoading = false;
      this.bind();
    }

    bind() {
      this.input.addEventListener('input', () => this.handleInput());
      // CHANGE-STOP (TOP-2-Layout): Liegt das Feld innerhalb von
      // <cart-drawer-items>, bubbelt das native change-Event zu Dawns
      // CartItems-Listener, der es als Mengenänderung wertet und den
      // Step-Fehler-Tooltip zeigt. Deshalb hier stoppen. Im Footer-
      // Layout ohne Effekt, dort fängt es niemand ab.
      this.input.addEventListener('change', (e) => e.stopPropagation());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          this.apply();
        }
      });
      this.submit.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.apply();
      });
      this.tagsEl.addEventListener('click', (e) => this.handleTagClick(e));
      this.setupPinchZoomBlock();
      this.handleInput();
    }

    /**
     * Disables pinch zoom ON THE DISCOUNT FORM (Dcart feature parity)
     * by cancelling multi-touch gestures that start on the form.
     *
     * SAFE BY DESIGN — this is NOT the viewport-meta approach that
     * previously swallowed the apply tap and displaced the caret:
     * - no viewport writes, no focus/blur handlers
     * - only touches with 2+ fingers are cancelled; single taps
     *   (input, apply button, remove-X) are untouched by definition
     * - scoped to this.root — pinch zoom elsewhere on the page keeps
     *   working (accessibility stays intact)
     * 'gesturestart' additionally covers iOS Safari's proprietary
     * pinch event.
     */
    setupPinchZoomBlock() {
      const blockMultiTouch = (e) => {
        if (e.touches && e.touches.length > 1) {
          e.preventDefault();
        }
      };
      this.root.addEventListener('touchstart', blockMultiTouch, { passive: false });
      this.root.addEventListener('touchmove', blockMultiTouch, { passive: false });
      this.root.addEventListener('gesturestart', (e) => e.preventDefault());
      this.root.addEventListener('gesturechange', (e) => e.preventDefault());
    }

    handleInput() {
      const value = this.input.value.trim();
      this.submit.disabled = value.length === 0 || this.isLoading;
      if (this.input.classList.contains('vast-discount__input--error') && value.length > 0) {
        this.input.classList.remove('vast-discount__input--error');
        this.clearMessage();
      }
    }

    /**
     * Runs one update attempt. HTTP rejection is treated as "code not
     * applied in this attempt" (returns null) — the caller decides what
     * to try next. Network failures always abort.
     */
    async tryUpdate(codes, sections) {
      try {
        return await updateDiscounts(codes, sections);
      } catch (e) {
        if (e && e.kind === 'network') throw this.makeError('network');
        return null; // http rejection → let the caller try the next strategy
      }
    }

    /**
     * Apply a code:
     * 1. Fetch current cart
     * 2. Attempt: existing applicable codes + new code (combine)
     * 3. If the new code was rejected: apply it ALONE → replaces the
     *    previous code(s). Entering a new ambassador code while another
     *    is active should switch codes, not error out.
     * 4. If it fails alone too → unified error, previous codes restored.
     * 5. On success: sync the discount cookie, blocklist any replaced
     *    codes, re-render Dawn sections.
     */
    async apply() {
      const code = (this.input.value || '').trim();
      if (!code) {
        this.showMessage(STRINGS.empty, 'error');
        return;
      }

      this.setLoading(true);
      this.clearMessage();

      try {
        // VAST (Okt 2026, Test): /cart.js-Vorabruf nur, wenn die Pills
        // aktive manuelle Codes zeigen. Ohne Pills (haeufigster Fall) geht
        // es direkt zu "allein anwenden" - spart einen Roundtrip. Die
        // Pills werden serverseitig aus dem Cart gerendert und nach jedem
        // Re-Render per ensurePills synchronisiert. Restrisiko: ein Code,
        // der nach dem letzten Render serverseitig dazukam (zweiter Tab,
        // Affiliate-App), wird ersetzt statt kombiniert.
        const pillCodes = this.getActivePillCodes();
        let cartBefore = null;
        let beforeApplicable = [];

        if (pillCodes.length > 0) {
          cartBefore = await fetchCart();
          beforeApplicable = (cartBefore.discount_codes || [])
            .filter(d => d.applicable)
            .map(d => d.code);
        }

        if (beforeApplicable.some(c => c.toUpperCase() === code.toUpperCase())) {
          throw this.makeError('already_applied');
        }

        const sections = getCartSections();
        let cartAfter = null;
        let applied = false;

        // Attempt 1: combine with existing codes (skip if none active)
        if (beforeApplicable.length > 0) {
          cartAfter = await this.tryUpdate([...beforeApplicable, code], sections);
          applied = cartAfter ? codeIsApplicable(cartAfter, code) : false;
        }

        // Attempt 2: apply alone — replaces previous code(s)
        if (!applied) {
          const cartAlone = await this.tryUpdate([code], sections);
          if (cartAlone) {
            applied = codeIsApplicable(cartAlone, code);
            cartAfter = cartAlone;
          }

          if (!applied) {
            // NOTE: The Storefront Cart API cannot distinguish a
            // nonexistent code from an existing-but-not-qualifying one —
            // Shopify persists ANY string as a discount code with
            // applicable:false. (Apps like Dcart resolve this via the
            // Admin API, which a theme cannot access.) We therefore show
            // ONE unified error message for both cases.
            //
            // Restore the previous state server-side — ALWAYS, also when
            // no codes were active before, to clear the failed code from
            // the cart (empty array → discount: '').
            // IMPORTANT: do NOT re-render here. The DOM never showed the
            // intermediate state, and a re-render would replace the form
            // element BEFORE the error message is written (message would
            // land on a detached node and never be visible).
            await this.tryUpdate(beforeApplicable, null);
            throw this.makeError('invalid');
          }
        }

        // SUCCESS — any dropped previous codes are intentional (replace):
        // blocklist them so auto-apply scripts can't force them back in,
        // and clear the affiliate traces they'd re-apply from.
        const afterUpper = (cartAfter.discount_codes || [])
          .filter(d => d.applicable)
          .map(d => d.code.toUpperCase());
        const dropped = beforeApplicable.filter(
          c => !afterUpper.includes(c.toUpperCase())
        );
        if (dropped.length > 0) {
          dropped.forEach(c => blockCode(c));
          clearAffiliateTraces();
        }
        unblockCode(code);
        this.syncDiscountCookie(code);

        this.input.value = '';
        this.handleInput();
        this.applySectionsAndRender(cartAfter);
        // VAST: ohne Vorabruf gibt es kein cartBefore - dann Gesamtrabatt
        // aus der Antwort als Naeherung
        const trackedValue = cartBefore
          ? (cartBefore.total_price - cartAfter.total_price) / 100
          : (cartAfter.total_discount || 0) / 100;
        this.trackEvent('discount_applied', { code, value: trackedValue });
        this.dispatch('vast:discount:applied', { code });
      } catch (err) {
        this.input.classList.add('vast-discount__input--error');
        this.showMessage(this.messageForReason(err.reason), this.severityForReason(err.reason));
        this.dispatch('vast:discount:error', { code, reason: err.reason });
        this.trackEvent('discount_failed', { code, reason: err.reason });
      } finally {
        this.setLoading(false);
      }
    }

    async handleTagClick(e) {
      const removeBtn = e.target.closest(SELECTORS.remove);
      if (!removeBtn) return;
      e.preventDefault();
      e.stopPropagation();

      const codeToRemove = removeBtn.dataset.vastDiscountRemove;
      if (!codeToRemove || this.isLoading) return;

      this.setLoading(true);
      this.clearMessage();

      try {
        const cart = await fetchCart();
        const remaining = (cart.discount_codes || [])
          .filter(d => d.applicable && d.code.toUpperCase() !== codeToRemove.toUpperCase())
          .map(d => d.code);

        const sections = getCartSections();
        const updated = await updateDiscounts(remaining, sections);

        // The user explicitly removed this code: blocklist it so
        // auto-apply scripts can't re-inject it, wipe their traces,
        // and sync the checkout cookie.
        blockCode(codeToRemove);
        clearAffiliateTraces();
        this.syncDiscountCookie(remaining[0] || null);

        this.applySectionsAndRender(updated);
        this.dispatch('vast:discount:removed', { code: codeToRemove });
      } catch (_) {
        this.showMessage(STRINGS.networkError, 'error');
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * VAST (Okt 2026): aktive manuelle Codes aus den gerenderten Pills.
     * Dient als Ersatz fuer den /cart.js-Vorabruf in apply(), wenn keine
     * Codes aktiv sind. Automatische Rabatte (data-type="automatic")
     * zaehlen nicht, sie sind keine Codes.
     */
    getActivePillCodes() {
      if (!this.tagsEl) return [];
      return Array.from(this.tagsEl.querySelectorAll(SELECTORS.tag + '[data-type="manual"]'))
        .map(el => (el.dataset.code || '').trim())
        .filter(Boolean);
    }

    makeError(reason) {
      const err = new Error(reason);
      err.reason = reason;
      return err;
    }

    messageForReason(reason) {
      switch (reason) {
        case 'not_combinable':  return STRINGS.notCombinable;
        case 'expired':         return STRINGS.expired;
        case 'minimum':         return STRINGS.minimumNotMet;
        case 'customer_only':   return STRINGS.customerOnly;
        case 'product':         return STRINGS.productMismatch;
        case 'usage_limit':     return STRINGS.usageLimit;
        case 'already_applied': return STRINGS.alreadyApplied;
        case 'network':         return STRINGS.networkError;
        case 'invalid':
        default:                return STRINGS.invalid;
      }
    }

    severityForReason(reason) {
      return reason === 'minimum' || reason === 'product' ? 'warning' : 'error';
    }

    setLoading(loading) {
      this.isLoading = loading;
      if (loading) {
        // Lock the current width BEFORE swapping the label for the
        // spinner. With labels wider than min-width (e.g. "Anwenden")
        // the button would otherwise collapse to min-width and visibly
        // shrink; short labels ("Apply") sit at min-width and never
        // moved — hence the "only in German" symptom.
        this.submit.style.width = this.submit.offsetWidth + 'px';
      } else {
        this.submit.style.width = '';
      }
      this.submit.disabled = loading || (this.input.value || '').trim().length === 0;
      this.submitLabel.hidden = loading;
      this.spinner.hidden = !loading;
      this.root.dataset.loading = loading ? 'true' : 'false';
      // Kein Unlock mehr hier: der Zoom-Lock hängt am offenen Drawer
      // (setupDrawerZoomLock) und wird erst beim Schließen aufgehoben.
    }

    showMessage(text, severity) {
      this.messageEl.textContent = text;
      this.messageEl.dataset.severity = severity;
      this.messageEl.hidden = false;
    }

    clearMessage() {
      this.messageEl.textContent = '';
      this.messageEl.hidden = true;
      delete this.messageEl.dataset.severity;
    }

    applySectionsAndRender(cart) {
      renderSections(cart);
    }

    /**
     * Sync Shopify's discount_code cookie with the manually applied code.
     *
     * Background: ambassador links (/discount/CODE, UpPromote redirects)
     * set a discount_code cookie. Our form only updates the CART — the
     * checkout however reads the COOKIE and re-applies the old code
     * even after the customer manually entered a different one.
     * Fetching /discount/{code} overwrites the cookie; passing null
     * clears it (used when the last code is removed).
     */
    syncDiscountCookie(code) {
      try {
        if (code) {
          fetch('/discount/' + encodeURIComponent(code), {
            credentials: 'same-origin',
          }).catch(() => {});
        } else {
          // Clear all plausible variants — Shopify may set the cookie
          // with or without a domain attribute.
          const host = window.location.hostname;
          const bareHost = host.replace(/^www\./, '');
          const expire = 'discount_code=; Max-Age=0; path=/';
          document.cookie = expire;
          document.cookie = expire + '; domain=' + host;
          document.cookie = expire + '; domain=.' + bareHost;
        }
      } catch (_) {}
    }

    dispatch(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }

    trackEvent(name, payload) {
      try {
        if (window.dataLayer && Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: name, ...payload });
        }
        if (typeof window.fbq === 'function') {
          const fbName = name === 'discount_applied' ? 'DiscountApplied' : 'DiscountFailed';
          window.fbq('trackCustom', fbName, payload);
        }
      } catch (_) {}
    }
  }

  /* ---------- Bootstrap ---------- */

  function init(scope) {
    (scope || document).querySelectorAll(SELECTORS.root).forEach(root => {
      if (root.dataset.vastInitialized === 'true') return;
      root.dataset.vastInitialized = 'true';
      new DiscountForm(root);
    });
  }

  function boot() {
    // The form lives inside <cart-drawer>; that is the only node whose
    // innerHTML gets swapped by Dawn's section rendering. Observing it
    // instead of document.body keeps the observer out of every
    // unrelated DOM mutation on the page (sliders, lazy images, apps).
    // Fallback to body only if the theme has no drawer element.
    const drawer = document.querySelector('cart-drawer');
    const scope = drawer || document.body;

    init(scope);
    setupDrawerZoomLock(drawer);
    setupPillSync(drawer);

    // The cart drawer re-renders its inner HTML after every cart
    // mutation. That wipes our event listeners. Re-init when new
    // instances appear. Records of one innerHTML swap arrive batched
    // in a single callback, so this runs once per re-render.
    new MutationObserver(() => {
      if (scope.querySelector(SELECTORS.root + ':not([data-vast-initialized="true"])')) {
        init(scope);
      }
    }).observe(scope, { childList: true, subtree: true });

    // Strip codes the user explicitly removed but an affiliate
    // auto-apply script re-injected (runs once per page load).
    // Bewusst SOFORT beim Boot, nicht verzögert: ein späterer Lauf
    // könnte sich mit einer Nutzereingabe überschneiden und einen
    // gerade gesetzten Code überschreiben. Läuft ohnehin nur, wenn
    // die Blocklist Einträge hat.
    enforceBlockedCodes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();